import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { fetchGalleryAlbums, galleryStoreEnabled } from '../services/galleryStore';

const groupIntoPets = (images, groupSize = 4) => {
  const groups = [];
  for (let i = 0; i < images.length; i += groupSize) {
    const chunk = images.slice(i, i + groupSize);
    if (!chunk.length) continue;
    groups.push({
      id: `pet-${String(groups.length + 1).padStart(2, '0')}`,
      title: `Pet ${String(groups.length + 1).padStart(2, '0')}`,
      subtitle: 'Gallery',
      images: chunk
    });
  }
  return groups;
};

const ensureDefaultAlbums = (defaultImages) => {
  const base = groupIntoPets(defaultImages, 4);
  return base;
};

export default function GalleryPage({ images }) {
  const [albums] = useState(() => ensureDefaultAlbums(images));
  const [remoteAlbums, setRemoteAlbums] = useState([]);
  const [remoteError, setRemoteError] = useState('');
  const [activeAlbum, setActiveAlbum] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const remoteEnabled = galleryStoreEnabled();

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!remoteEnabled) return;
      try {
        const list = await fetchGalleryAlbums();
        if (cancelled) return;
        setRemoteAlbums(list);
      } catch (e) {
        if (cancelled) return;
        setRemoteError(e?.message || 'Failed to load gallery.');
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [remoteEnabled]);

  const combinedAlbums = useMemo(() => {
    if (!remoteEnabled) return albums;
    return remoteAlbums.map((a) => ({
      id: a.id,
      title: a.title,
      subtitle: a.category || 'Gallery',
      images: a.images || []
    }));
  }, [albums, remoteAlbums, remoteEnabled]);

  const allCount = useMemo(
    () => combinedAlbums.reduce((sum, a) => sum + (a.images?.length || 0), 0),
    [combinedAlbums]
  );

  return (
    <div className="page gallery">
      <div className="gallery-inner">
        <motion.div
          className="gallery-head"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55 }}
        >
          <div className="gallery-kicker">Gallery</div>
          <h1>Pet Gallery</h1>
          <p>
            Each pet can have multiple photos. Tap an album to view more.
          </p>
          <div className="gallery-actions">
            <div className="gallery-meta">{allCount} photos</div>
          </div>
          {remoteError ? <div className="gallery-error">{remoteError}</div> : null}
        </motion.div>

        <div className="gallery-grid" aria-label="Pet gallery">
          {remoteEnabled && !remoteError && combinedAlbums.length === 0 ? (
            <div className="gallery-empty" role="status">
              <div className="gallery-empty-title">No gallery albums yet</div>
              <div className="gallery-empty-sub">Albums will appear here after the owner uploads photos in the admin portal.</div>
            </div>
          ) : null}
          {combinedAlbums.map((album, albumIdx) => (
            <motion.section
              key={album.id}
              className="gallery-album"
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: Math.min(0.18, albumIdx * 0.03) }}
            >
              <button
                className="gallery-album-card"
                type="button"
                onClick={() => {
                  setActiveAlbum(album);
                  setActiveIndex(0);
                }}
                aria-label={`Open ${album.title}`}
              >
                <div className="gallery-album-cover">
                  {album.images?.[0] ? <img src={album.images[0]} alt={album.title} loading="lazy" /> : <div className="photo-placeholder" aria-hidden="true" />}
                  <div className="gallery-album-badge">{album.images.length} photos</div>
                </div>
                <div className="gallery-album-meta">
                  <div className="gallery-album-title">{album.title}</div>
                  <div className="gallery-album-sub">{album.subtitle || 'Photos'}</div>
                </div>
                <div className="gallery-album-thumbs" aria-hidden="true">
                  {album.images.slice(1, 4).map((src, idx) => (
                    <div className="gallery-thumb" key={`${album.id}-t-${idx}`}>
                      <img src={src} alt="" loading="lazy" />
                    </div>
                  ))}
                </div>
              </button>
            </motion.section>
          ))}
        </div>
      </div>

      {activeAlbum ? (
        <div
          className="gallery-lightbox"
          role="dialog"
          aria-label="Album viewer"
          onClick={() => setActiveAlbum(null)}
        >
          <button className="gallery-lightbox-close" type="button" onClick={() => setActiveAlbum(null)}>
            ✕
          </button>
          <div className="gallery-lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <div className="gallery-lightbox-head">
              <div className="gallery-lightbox-title">{activeAlbum.title}</div>
              <div className="gallery-lightbox-sub">{activeAlbum.images.length} photos</div>
            </div>
            <div className="gallery-viewer">
              <div className="gallery-viewer-main">
                <motion.img
                  key={`${activeAlbum.id}-active-${activeIndex}`}
                  src={activeAlbum.images[activeIndex]}
                  alt="Pet"
                  initial={{ opacity: 0, y: 10, scale: 0.99 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.35 }}
                />
              </div>
              <div className="gallery-viewer-thumbs" aria-label="Choose photo">
                {activeAlbum.images.map((src, idx) => (
                  <button
                    key={`${activeAlbum.id}-thumb-${idx}`}
                    type="button"
                    className={`gallery-viewer-thumb ${idx === activeIndex ? 'is-active' : ''}`}
                    onClick={() => setActiveIndex(idx)}
                    aria-label={`Show photo ${idx + 1}`}
                  >
                    <img src={src} alt="" loading="lazy" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
